import { ReactElement } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { Field, HTMLLink, LayoutServiceData, Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import { sanitize } from 'isomorphic-dompurify';
import { lowerCase } from 'lodash';
import localFont from 'next/font/local';
import Head from 'next/head';
import Script from 'next/script';

import { ImageValue } from '@gn-audio/jss-components';

import { useAppSettings } from 'common/appSettings/AppSettingContext';
import { LanguageType } from 'common/appSettings/appSettings.model';
import { CondensedMenuProvider } from 'common/condensedMenu/CondensedMenuProvider';
import { IJabraTheme } from 'common/jabraTheme/JabraTheme.models';
import { JabraThemeProvider } from 'common/jabraTheme/JabraThemeContext';
import { useJabraThemesList } from 'common/jabraTheme/JabraThemesListContext';
import { PageProductProvider } from 'common/PageProductContext/pageProductContext';
import { ScrollerProvider } from 'common/scrolling/scrollerContext';
import { TopSection } from 'components/TopSection';
import Scripts from 'src/Scripts';

import { TitleDictionary } from './sitecoreModels/Product';
import { PageMetaTags } from './PageMetaTags';
import { SpotWrapper } from './SpotWrapper';

export const GNElliotWeb = localFont({
  src: [
    {
      path: './fonts/GNElliotWeb-Light.woff2',
      weight: '300',
      style: 'normal',
    },
    {
      path: './fonts/GNElliotWeb-Regular.woff2',
      weight: '400',
      style: 'normal',
    },
    {
      path: './fonts/GNElliotWeb-Bold.woff2',
      weight: '600',
      style: 'normal',
    },
    {
      path: './fonts/GNElliotWeb-Bold.woff2',
      weight: '700',
      style: 'normal',
    },
    {
      path: './fonts/GNElliotWeb-Heavy.woff2',
      weight: '900',
      style: 'normal',
    },
  ],
});

interface LayoutProps {
  layoutData: LayoutServiceData;
  headLinks: HTMLLink[];
  publicUrl: string;
}

export interface RouteFields {
  [key: string]: unknown;

  PageTitle: Field<string>;
  Theme?: Field<string>;
  PageTitleDictionary?: TitleDictionary;
  PageDescription: Field<string>;
  PageDescriptionDictionary?: TitleDictionary;
  OpenGraphImage: Field<ImageValue>;
  EnableSmoothScroll: Field<boolean>;
  NoFollow?: Field<boolean>;
  NoIndex?: Field<boolean>;
  redirectTo: Field<string>;
  redirectToShared: Field<string>;
  isPermanentlyMovedShared: Field<boolean>;
  SelectedSites: LanguageType[];
}

const Layout = ({ layoutData, headLinks, publicUrl }: LayoutProps): JSX.Element => {
  const { route, context } = layoutData.sitecore;

  const jabraThemes = useJabraThemesList();
  const appSettings = useAppSettings();

  const fields = route?.fields as RouteFields;

  const jabraSitecoreTheme = jabraThemes.find(
    (theme) => lowerCase(theme?.name) === lowerCase(fields.Theme?.value)
  );

  const jabraTheme: IJabraTheme | undefined = jabraSitecoreTheme
    ? {
        backgroundColor: jabraSitecoreTheme.backgroundColor?.value,
        foregroundColor: jabraSitecoreTheme.foregroundColor?.value,
        darkTheme: !!jabraSitecoreTheme.darkTheme?.value,
      }
    : undefined;

  const isSmoothScrollEnabled = fields.EnableSmoothScroll?.value;

  return (
    <>
      <PageProductProvider>
        <Scripts />

        <TopSection nodes={appSettings?.topSectionParsed} />
        <Head>
          {headLinks.map((headLink) => (
            <link rel={headLink.rel} key={headLink.href} href={headLink.href} />
          ))}

          {appSettings?.topStyles?.map((css, idx) => {
            return (
              <style key={idx} dangerouslySetInnerHTML={{ __html: sanitize(css.style.value) }} />
            );
          })}
        </Head>
        <PageMetaTags
          fields={fields}
          currentLanguage={context.language || ''}
          selectedLanguages={appSettings?.selectedLanguages || []}
          selectedRegions={appSettings?.selectedRegions || []}
          publicUrl={publicUrl || ''}
        />
        {appSettings?.topScripts.map((js) => {
          return (
            <Script
              id={js.id}
              key={js.id}
              dangerouslySetInnerHTML={{ __html: sanitize(js.script.value) }}
            />
          );
        })}
        {appSettings?.bottomScripts.map((js) => {
          return (
            <Script
              strategy="afterInteractive"
              id={js.id}
              key={js.id}
              dangerouslySetInnerHTML={{ __html: sanitize(js.script.value) }}
            />
          );
        })}
        {appSettings?.topSectionParsed
          ?.filter(({ tagName }) => tagName === 'script')
          .map(({ props, content }, idx) => {
            return (
              <Script
                key={idx}
                {...props}
                dangerouslySetInnerHTML={{ __html: sanitize(content ?? '') }}
              />
            );
          })}
        {appSettings?.bottomSectionParsed
          ?.filter(({ tagName }) => tagName === 'script')
          .map(({ props, content }, idx) => {
            return (
              <Script
                key={idx}
                strategy="afterInteractive"
                {...props}
                dangerouslySetInnerHTML={{ __html: sanitize(content ?? '') }}
              />
            );
          })}
        <CondensedMenuProvider>
          <ScrollerProvider isSmoothScrollEnabled={isSmoothScrollEnabled}>
            <JabraThemeProvider theme={jabraTheme}>
              <div id="page-container-wrapper" className="m-auto max-w-[1920px]">
                <div
                  id="smooth-wrapper"
                  className={`page-container ${GNElliotWeb.className} ${RemoveScroll.classNames.zeroRight}`}
                >
                  <div id="smooth-content">
                    {/* root placeholder for the app, which we add components to using route data */}
                    <div>
                      {route && route?.placeholders['jss-header'] && (
                        <Placeholder name="jss-header" rendering={route} />
                      )}
                    </div>

                    <main>
                      {route && route?.placeholders['jss-main'] && (
                        <Placeholder
                          name="jss-main"
                          rendering={route}
                          render={(components, renderings) => {
                            return (
                              <div className="flex flex-col">
                                {components.map((component: ReactElement, idx) => {
                                  const rendering = renderings[idx];
                                  if (!('componentName' in rendering)) {
                                    return component;
                                  }
                                  return (
                                    <SpotWrapper
                                      key={rendering.uid}
                                      component={component}
                                      rendering={rendering}
                                    />
                                  );
                                })}
                              </div>
                            );
                          }}
                        />
                      )}
                    </main>
                    <div>
                      {route && route?.placeholders['footer'] && (
                        <Placeholder name="footer" rendering={route} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {appSettings?.bottomSectionParsed
                ?.filter(({ tagName }) => tagName === 'style')
                .map((node, idx) => {
                  return (
                    <style
                      key={idx + node.tagName}
                      {...node.props}
                      dangerouslySetInnerHTML={{ __html: sanitize(node.content ?? '') }}
                    />
                  );
                })}
            </JabraThemeProvider>
          </ScrollerProvider>
        </CondensedMenuProvider>
      </PageProductProvider>
      {appSettings?.bottomStyles?.map((css, idx) => {
        return <style key={idx} dangerouslySetInnerHTML={{ __html: sanitize(css.style.value) }} />;
      })}
    </>
  );
};
export default Layout;
